import { useCallback, useEffect, useState } from "react";
import { ApiError, apiGet, apiPost, apiPut } from "../lib/api";
import type {
  OutreachCampaign,
  OutreachCampaignMode,
  OutreachCampaignType,
  OutreachChannel,
  OutreachChannelStrategy,
} from "../lib/types";
import { useAuth } from "../auth/AuthContext";
import { useScope } from "../scope/ScopeContext";
import { OutreachStatusStrip } from "../components/OutreachStatusStrip";

/**
 * Per-dealer outreach campaign settings — one Sales and one Service campaign per
 * rooftop, each with its own mode (off / approval / automatic), delay, cooldowns,
 * channels, templates, threshold, and the hard-gate toggle.
 */

// Every campaign type needs a label even though this screen renders only the
// two it manages (see the hard-coded ["sales", "service"] below): the map is
// keyed by the full union, and a missing key renders `undefined` as a heading
// rather than failing anywhere you would notice.
const TYPE_LABEL: Record<OutreachCampaignType, string> = {
  sales: "Sales",
  service: "Service",
  vehicle_acquisition: "Vehicle acquisition",
  unsold_visit: "Ups",
};

const MODE_HELP: Record<OutreachCampaignMode, string> = {
  off: "Worker ignores this campaign entirely.",
  approval: "Pulls customers, but nothing sends until you approve each batch.",
  automatic: "Pulls AND sends without human review.",
};

type Draft = Partial<OutreachCampaign> & { dealerId: string; type: OutreachCampaignType };

function draftFrom(campaign: OutreachCampaign | undefined, dealerId: string, type: OutreachCampaignType): Draft {
  if (campaign) return { ...campaign };
  return {
    dealerId,
    type,
    name: `${TYPE_LABEL[type]} review requests`,
    mode: "off",
    channels: ["email"],
    channelStrategy: "simultaneous",
    recencyWindowDays: 30,
    minDaysSinceEvent: 1,
    cooldownDays: 90,
    crossTypeCooldownDays: 90,
    smsTemplate: null,
    emailSubjectTemplate: null,
    emailBodyTemplate: null,
    reviewUrl: null,
    yelpReviewUrl: null,
    incentiveText: null,
    satisfactionThreshold: 4,
    hardGateBelowThreshold: false,
    dailySendCap: 200,
    sendRatePerHour: null,
    sendWindowStartHour: null,
    sendWindowEndHour: null,
  };
}

// Global fallbacks shown as placeholders when a campaign hasn't overridden them.
const FREQ_DEFAULTS = { ratePerHour: 20, windowStartHour: 9, windowEndHour: 18 };

export function OutreachSettingsPage() {
  const { user } = useAuth();
  const { focusedDealerId, dealers, dealersInFocus } = useScope();
  const [campaigns, setCampaigns] = useState<OutreachCampaign[]>([]);
  const [loading, setLoading] = useState(true);

  // Which dealer are we editing? Focus first, then the user's own dealer, then first visible.
  const dealerId = focusedDealerId ?? user?.dealerId ?? dealersInFocus[0]?.id ?? dealers[0]?.id;
  const dealer = dealers.find((d) => d.id === dealerId);

  const load = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    try {
      const res = await apiGet<{ campaigns: OutreachCampaign[] }>(
        `/api/outreach/campaigns?dealerId=${dealerId}`
      );
      setCampaigns(res.campaigns);
    } catch (err) {
      console.error("outreach campaigns load failed:", err);
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!dealerId) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-8 sm:py-8 text-sm text-slate-500">
        No dealer visible in your scope.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-8 sm:py-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Outreach settings</h1>
        <p className="mt-1 text-sm text-slate-500">
          Review-request campaigns for{" "}
          <span className="font-medium text-slate-700">{dealer?.name ?? "this store"}</span>. Sales
          asks recent buyers; Service asks recent service customers. Use the store picker to switch
          rooftops.
        </p>
      </div>

      <OutreachStatusStrip />

      {loading ? (
        <div className="mt-6 text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
          {(["sales", "service"] as const).map((type) => (
            <CampaignEditor
              key={`${dealerId}:${type}`}
              type={type}
              initial={draftFrom(
                campaigns.find((c) => c.type === type),
                dealerId,
                type
              )}
              onSaved={() => void load()}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CampaignEditor({
  type,
  initial,
  onSaved,
}: {
  type: OutreachCampaignType;
  initial: Draft;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(initial);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Re-sync when the dealer (or loaded campaign) changes.
  useEffect(() => setDraft(initial), [initial.dealerId, initial.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  const toggleChannel = (ch: OutreachChannel) => {
    const channels = draft.channels ?? [];
    const next = channels.includes(ch) ? channels.filter((c) => c !== ch) : [...channels, ch];
    if (next.length === 0) return; // at least one channel
    set({ channels: next });
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name: draft.name,
        mode: draft.mode,
        channels: draft.channels,
        channelStrategy: draft.channelStrategy ?? "simultaneous",
        recencyWindowDays: draft.recencyWindowDays,
        minDaysSinceEvent: draft.minDaysSinceEvent,
        cooldownDays: draft.cooldownDays,
        crossTypeCooldownDays: draft.crossTypeCooldownDays,
        smsTemplate: draft.smsTemplate || null,
        emailSubjectTemplate: draft.emailSubjectTemplate || null,
        emailBodyTemplate: draft.emailBodyTemplate || null,
        reviewUrl: draft.reviewUrl || null,
        yelpReviewUrl: draft.yelpReviewUrl || null,
        incentiveText: draft.incentiveText?.trim() || null,
        satisfactionThreshold: draft.satisfactionThreshold,
        hardGateBelowThreshold: draft.hardGateBelowThreshold,
        dailySendCap: draft.dailySendCap,
        sendRatePerHour: draft.sendRatePerHour ?? null,
        sendWindowStartHour: draft.sendWindowStartHour ?? null,
        sendWindowEndHour: draft.sendWindowEndHour ?? null,
      };
      if (draft.id) {
        await apiPut(`/api/outreach/campaigns/${draft.id}`, body);
      } else {
        await apiPost("/api/outreach/campaigns", { ...body, dealerId: draft.dealerId, type });
      }
      setSavedAt(Date.now());
      onSaved();
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);
      alert(`Save failed: ${message}`);
    } finally {
      setBusy(false);
    }
  };

  const mode = draft.mode ?? "off";

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900">{TYPE_LABEL[type]}</h2>
          <ModeBadge mode={mode} />
        </div>
        {draft.lastRunAt && (
          <span className="text-[11px] text-slate-400">
            Last run {new Date(draft.lastRunAt).toLocaleString()}
          </span>
        )}
      </div>

      <label className="mt-4 block">
        <span className="text-xs font-medium text-slate-700">Mode</span>
        <select
          value={mode}
          onChange={(e) => set({ mode: e.target.value as OutreachCampaignMode })}
          className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-inner"
        >
          <option value="off">Off</option>
          <option value="approval">On — approve each batch</option>
          <option value="automatic">Automatic — send without review</option>
        </select>
        <span className="mt-1 block text-[11px] text-slate-500">{MODE_HELP[mode]}</span>
      </label>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <NumberField
          label={`Days after ${type === "sales" ? "sale" : "service"} before asking`}
          value={draft.minDaysSinceEvent ?? 1}
          min={0}
          max={90}
          onChange={(n) => set({ minDaysSinceEvent: n })}
        />
        <NumberField
          label="Look-back window (days)"
          value={draft.recencyWindowDays ?? 30}
          min={1}
          max={365}
          onChange={(n) => set({ recencyWindowDays: n })}
        />
        <NumberField
          label="Re-ask cooldown (days)"
          value={draft.cooldownDays ?? 90}
          min={0}
          max={730}
          onChange={(n) => set({ cooldownDays: n })}
        />
        <NumberField
          label="Cooldown after other campaign asked (days)"
          value={draft.crossTypeCooldownDays ?? 90}
          min={0}
          max={730}
          onChange={(n) => set({ crossTypeCooldownDays: n })}
        />
      </div>

      <fieldset className="mt-4">
        <legend className="text-xs font-medium text-slate-700">Channels</legend>
        <div className="mt-2 flex gap-2">
          {(["email", "sms"] as const).map((ch) => (
            <label
              key={ch}
              className={`cursor-pointer rounded-lg border px-3 py-1.5 text-xs font-medium ${
                (draft.channels ?? []).includes(ch)
                  ? "border-brand-500 bg-brand-50 text-brand-700"
                  : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <input
                type="checkbox"
                checked={(draft.channels ?? []).includes(ch)}
                onChange={() => toggleChannel(ch)}
                className="sr-only"
              />
              {ch === "email" ? "Email" : "Text (SMS)"}
            </label>
          ))}
        </div>
        {(draft.channels ?? []).length > 1 && (
          <label className="mt-2 block">
            <span className="text-xs font-medium text-slate-700">Send order</span>
            <select
              value={draft.channelStrategy ?? "simultaneous"}
              onChange={(e) => set({ channelStrategy: e.target.value as OutreachChannelStrategy })}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-inner"
            >
              <option value="simultaneous">Simultaneous — fire every channel at once</option>
              <option value="priority_fallback">
                Priority — try {(draft.channels ?? [])[0] === "sms" ? "text" : "email"} first, fall
                back only if unreachable
              </option>
            </select>
          </label>
        )}
      </fieldset>

      <fieldset className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <legend className="px-1 text-xs font-medium text-slate-700">Sending frequency (trickle)</legend>
        <p className="mb-2 text-[11px] leading-snug text-slate-500">
          Approved customers drip out at this rate instead of all at once — applies to both
          individual and bulk sends. Leave blank to use the company default
          (~{FREQ_DEFAULTS.ratePerHour}/hr, {FREQ_DEFAULTS.windowStartHour}:00–
          {FREQ_DEFAULTS.windowEndHour}:00). Individual “Send now” in the queue ignores this and goes
          immediately.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <OptNumberField
            label="Messages / hour"
            value={draft.sendRatePerHour ?? null}
            placeholder={FREQ_DEFAULTS.ratePerHour}
            min={1}
            max={500}
            onChange={(n) => set({ sendRatePerHour: n })}
          />
          <OptNumberField
            label="Send from (hour, 0–23)"
            value={draft.sendWindowStartHour ?? null}
            placeholder={FREQ_DEFAULTS.windowStartHour}
            min={0}
            max={23}
            onChange={(n) => set({ sendWindowStartHour: n })}
          />
          <OptNumberField
            label="Send until (hour, 1–24)"
            value={draft.sendWindowEndHour ?? null}
            placeholder={FREQ_DEFAULTS.windowEndHour}
            min={1}
            max={24}
            onChange={(n) => set({ sendWindowEndHour: n })}
          />
        </div>
      </fieldset>

      <label className="mt-4 block">
        <span className="text-xs font-medium text-slate-700">Email subject</span>
        <input
          type="text"
          value={draft.emailSubjectTemplate ?? ""}
          onChange={(e) => set({ emailSubjectTemplate: e.target.value })}
          placeholder="How was your experience at {rooftop}?"
          className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-inner"
        />
      </label>
      <label className="mt-3 block">
        <span className="text-xs font-medium text-slate-700">Email body</span>
        <textarea
          rows={4}
          value={draft.emailBodyTemplate ?? ""}
          onChange={(e) => set({ emailBodyTemplate: e.target.value })}
          placeholder={"Hi {firstName},\n\nThank you for choosing {rooftop}… {link}"}
          className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-inner"
        />
      </label>
      <label className="mt-3 block">
        <span className="text-xs font-medium text-slate-700">SMS message</span>
        <textarea
          rows={2}
          value={draft.smsTemplate ?? ""}
          onChange={(e) => set({ smsTemplate: e.target.value })}
          placeholder="Hi {firstName}, thanks for choosing {rooftop}! It takes 30 seconds: {link}"
          className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-inner"
        />
        <span className="mt-1 block text-[11px] text-slate-500">
          Placeholders: <code>{"{firstName}"}</code> <code>{"{rooftop}"}</code>{" "}
          <code>{"{link}"}</code>. Blank fields fall back to sensible defaults. "Reply STOP to opt
          out." is appended to SMS automatically.
        </span>
      </label>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Google review URL</span>
          <input
            type="text"
            value={draft.reviewUrl ?? ""}
            onChange={(e) => set({ reviewUrl: e.target.value })}
            placeholder="https://g.page/r/…/review"
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-xs shadow-inner"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Yelp review URL</span>
          <input
            type="text"
            value={draft.yelpReviewUrl ?? ""}
            onChange={(e) => set({ yelpReviewUrl: e.target.value })}
            placeholder="https://www.yelp.com/writeareview/biz/…"
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-xs shadow-inner"
          />
          <span className="mt-1 block text-[11px] text-slate-500">
            Blank hides the "do you have a Yelp account?" question entirely — everyone goes to
            Google. Note Yelp's guidelines ask businesses not to solicit reviews.
          </span>
        </label>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <NumberField
          label="Daily send cap"
          value={draft.dailySendCap ?? 200}
          min={1}
          max={2000}
          onChange={(n) => set({ dailySendCap: n })}
        />
      </div>

      <label className="mt-4 block">
        <span className="text-xs font-medium text-slate-700">
          Incentive line (shown on the last step)
        </span>
        <textarea
          value={draft.incentiveText ?? ""}
          onChange={(e) => set({ incentiveText: e.target.value })}
          rows={2}
          maxLength={500}
          placeholder="You are now automatically entered into the gas gift card drawings…"
          className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-xs shadow-inner"
        />
        <span className="mt-1 block text-[11px] text-slate-500">
          Shown to <strong>everyone</strong> who completes the survey — including customers who
          answered "No" — and never behind the review link. An entry earned by finishing the survey
          is allowed; an entry earned by leaving a positive review is not (Google's incentivized-review
          ban, FTC Consumer Review Rule).
        </span>
      </label>

      <label className="mt-4 block">
        <span className="text-xs font-medium text-slate-700">
          Happy threshold (stars ≥ this go to the review ask)
        </span>
        <select
          value={draft.satisfactionThreshold ?? 4}
          onChange={(e) => set({ satisfactionThreshold: Number(e.target.value) })}
          className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-inner"
        >
          {[3, 4, 5].map((n) => (
            <option key={n} value={n}>
              {n}+ stars
            </option>
          ))}
        </select>
      </label>

      <div
        className={`mt-4 rounded-xl border p-3 ${
          draft.hardGateBelowThreshold ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-slate-50"
        }`}
      >
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={draft.hardGateBelowThreshold ?? false}
            onChange={(e) => set({ hardGateBelowThreshold: e.target.checked })}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
          />
          <span>
            <span className="block text-sm font-medium text-slate-900">
              Hard gate: unhappy customers get the manager form ONLY (no review link)
            </span>
            <span className="mt-1 block text-[11px] leading-snug text-rose-700">
              Warning — hiding the review link based on score ("review gating") violates Google's
              review policies and FTC Consumer Review Rule guidance; it can get this store's reviews
              filtered or removed. Off = compliant flow (everyone sees the review link; low scores
              also get the manager form).
            </span>
          </span>
        </label>
      </div>

      <div className="mt-5 flex items-center justify-end gap-3">
        {savedAt && <span className="text-[11px] text-emerald-600">Saved.</span>}
        <button
          onClick={() => void save()}
          type="button"
          disabled={busy}
          className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {busy ? "Saving…" : draft.id ? "Save changes" : "Create campaign"}
        </button>
      </div>
    </section>
  );
}

function ModeBadge({ mode }: { mode: OutreachCampaignMode }) {
  const styles: Record<OutreachCampaignMode, string> = {
    off: "bg-slate-100 text-slate-500 ring-slate-200",
    approval: "bg-amber-50 text-amber-700 ring-amber-200",
    automatic: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${styles[mode]}`}
    >
      {mode}
    </span>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-inner"
      />
    </label>
  );
}

/** Number input whose empty state means "unset → use the default" (shown as placeholder). */
function OptNumberField({
  label,
  value,
  placeholder,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number | null;
  placeholder: number;
  min: number;
  max: number;
  onChange: (n: number | null) => void;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <input
        type="number"
        value={value ?? ""}
        placeholder={`${placeholder} (default)`}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-inner"
      />
    </label>
  );
}
